import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { environment } from '../../../../environments/environment';
import { getAvoidErrorHandlingHeader } from '../base/headers';
import { CompareScenariosRequest, CompareScenariosResponse } from '../../models/compare-scenarios';
import { PlanAcceptRequest } from '../../models/plan-accept-request';
import { PlanAcceptResponse } from '../../models/plan-accept-response';
import { PlanGenerationInput } from '../../models/plan-generation-input';
import { PlanListWeightsQueryParams } from '../../models/plan-list-weights-query-params';
import { PlanLoadQueryParams } from '../../models/plan-load-query-params';
import { PlanLoadResponse } from '../../models/plan-load-response';
import { PlanOf } from '../../models/plan-of';
import { PlanPresetsResponse } from '../../models/plan-presets-response';
import { PlanResult } from '../../models/plan-result';
import { PlanWeightConfig } from '../../models/plan-weight-config';
import { PlanWeightSaveRequest } from '../../models/plan-weight-save-request';
import { AlertsResponse } from '../../models/alerts';
import { PrioritiesResponse } from '../../models/priorities';
import { WhatIfRequest, WhatIfResponse } from '../../models/what-if';
import { ChangeLogPageResponse, ChangeLogQueryParams } from '../../models/change-log';
import { CncProgramsResponse, ProgramStatus } from '../../models/cnc-programs';
import { QualityPlansResponse, QualityStatus } from '../../models/quality-plans';
import { ProductionMoveRequest, ProductionMoveResponse, ProductionPlanResponse } from '../../models/production-plan';
import { ReadinessBoardResponse } from '../../models/readiness-board';
import { PurchasingRawMaterialsResponse } from '../../models/purchasing-raw-materials';
import { SpecialToolOrdersResponse } from '../../models/special-tool-orders';

@Injectable()
export class PlannerApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${ environment.apiUrl }planner`;
  }

  simulate(input: PlanGenerationInput): Observable<PlanResult> {
    return this.httpClient.post<PlanResult>(`${ this.apiUrl }/simulate`, input);
  }

  acceptPlan(req: PlanAcceptRequest): Observable<PlanAcceptResponse> {
    return this.httpClient.post<PlanAcceptResponse>(`${ this.apiUrl }/accept`, req);
  }

  getLoad(params: PlanLoadQueryParams): Observable<PlanLoadResponse> {
    return this.httpClient.get<PlanLoadResponse>(`${ this.apiUrl }/load`,
      { params: this.buildParams(params) });
  }

  listWeights(params?: PlanListWeightsQueryParams): Observable<PlanPresetsResponse> {
    return this.httpClient.get<PlanPresetsResponse>(`${ this.apiUrl }/weights`,
      { params: this.buildParams(params) });
  }

  getPlannableOfs(): Observable<PlanOf[]> {
    return this.httpClient.get<PlanOf[]>(`${ this.apiUrl }/plannable-ofs`);
  }

  getPurchasingRawMaterials(onlyPending?: boolean): Observable<PurchasingRawMaterialsResponse> {
    return this.httpClient.get<PurchasingRawMaterialsResponse>(`${ this.apiUrl }/purchasing/raw-materials`,
      { params: this.buildParams(onlyPending ? { onlyPending: true } : {}) });
  }

  getPurchasingTooling(): Observable<SpecialToolOrdersResponse> {
    return this.httpClient.get<SpecialToolOrdersResponse>(`${ this.apiUrl }/purchasing/tooling`);
  }

  getCncPrograms(): Observable<CncProgramsResponse> {
    return this.httpClient.get<CncProgramsResponse>(`${ this.apiUrl }/technical-office/cnc-programs`);
  }

  setCncProgramStatus(ofHash: string, operationCode: string, status: ProgramStatus): Observable<void> {
    return this.httpClient.put<void>(`${ this.apiUrl }/technical-office/cnc-programs/status`,
      { ofHash, operationCode, status });
  }

  getQualityPlans(): Observable<QualityPlansResponse> {
    return this.httpClient.get<QualityPlansResponse>(`${ this.apiUrl }/quality/plans`);
  }

  setQualityPlanStatus(ofHash: string, status: QualityStatus): Observable<void> {
    return this.httpClient.put<void>(`${ this.apiUrl }/quality/plans/status`, { ofHash, status });
  }

  getProductionPlan(): Observable<ProductionPlanResponse> {
    return this.httpClient.get<ProductionPlanResponse>(`${ this.apiUrl }/production/plan`);
  }

  moveProductionTask(taskVersionHash: string, targetMachineHash: string, targetStartTime: number,
                     reason?: string, emergencyOverride?: boolean): Observable<ProductionMoveResponse> {
    const body = new ProductionMoveRequest(taskVersionHash, targetMachineHash, targetStartTime, reason, emergencyOverride);
    // Avoid the global error toast (raw error.<code>); the view shows its own message.
    return this.httpClient.post<ProductionMoveResponse>(`${ this.apiUrl }/production/move`, body,
      { headers: getAvoidErrorHandlingHeader() });
  }

  getReadinessBoard(): Observable<ReadinessBoardResponse> {
    return this.httpClient.get<ReadinessBoardResponse>(`${ this.apiUrl }/readiness/board`);
  }

  getChangeLog(params: ChangeLogQueryParams): Observable<ChangeLogPageResponse> {
    return this.httpClient.get<ChangeLogPageResponse>(`${ this.apiUrl }/change-log`,
      { params: this.buildParams(params) });
  }

  getAlerts(): Observable<AlertsResponse> {
    return this.httpClient.get<AlertsResponse>(`${ this.apiUrl }/alerts`);
  }

  getPriorities(): Observable<PrioritiesResponse> {
    return this.httpClient.get<PrioritiesResponse>(`${ this.apiUrl }/priorities`);
  }

  simulateWhatIf(req: WhatIfRequest): Observable<WhatIfResponse> {
    // Avoid the global error toast: the view shows validation errors in place.
    return this.httpClient.post<WhatIfResponse>(`${ this.apiUrl }/what-if`, req,
      { headers: getAvoidErrorHandlingHeader() });
  }

  compareScenarios(req: CompareScenariosRequest): Observable<CompareScenariosResponse> {
    // Avoid the global error toast: the view shows validation errors in place.
    return this.httpClient.post<CompareScenariosResponse>(`${ this.apiUrl }/compare-scenarios`, req,
      { headers: getAvoidErrorHandlingHeader() });
  }

  setOfUrgency(ofHash: string, active: boolean): Observable<void> {
    return this.httpClient.put<void>(`${ this.apiUrl }/priorities/${ ofHash }/urgency`, { active },
      { headers: getAvoidErrorHandlingHeader() });
  }

  setOfClaim(ofHash: string, active: boolean): Observable<void> {
    return this.httpClient.put<void>(`${ this.apiUrl }/priorities/${ ofHash }/claim`, { active },
      { headers: getAvoidErrorHandlingHeader() });
  }

  createWeights(req: PlanWeightSaveRequest): Observable<PlanWeightConfig> {
    return this.httpClient.post<PlanWeightConfig>(`${ this.apiUrl }/weights`, req);
  }

  updateWeights(hash: string, req: PlanWeightSaveRequest): Observable<PlanWeightConfig> {
    return this.httpClient.put<PlanWeightConfig>(`${ this.apiUrl }/weights/${ hash }`, req);
  }

  deleteWeights(hash: string): Observable<void> {
    return this.httpClient.delete<void>(`${ this.apiUrl }/weights/${ hash }`);
  }

  activateWeights(hash: string): Observable<PlanWeightConfig> {
    return this.httpClient.put<PlanWeightConfig>(`${ this.apiUrl }/weights/${ hash }/activate`, {});
  }

  deactivateWeights(hash: string): Observable<PlanWeightConfig> {
    return this.httpClient.put<PlanWeightConfig>(`${ this.apiUrl }/weights/${ hash }/deactivate`, {});
  }

  setDefaultWeights(hash: string): Observable<PlanWeightConfig> {
    return this.httpClient.put<PlanWeightConfig>(`${ this.apiUrl }/weights/${ hash }/default`, {});
  }

  private buildParams(obj: any): HttpParams {
    let p = new HttpParams();
    Object.keys(obj || {}).forEach((key) => {
      const value = obj[key];
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v) => { p = p.append(key, String(v)); });
        } else {
          p = p.set(key, String(value));
        }
      }
    });
    return p;
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/planner-api.service.ts